import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, Flex, HStack, Icon, IconButton, Select, Text } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import { Clock, CircleX, CircleCheck, Undo2, LayoutList, SquareKanban, CircleHelp, Zap, FileText, User, Truck, Store, Handshake, Boxes, Warehouse, Calendar, Columns3, ShoppingCart, PackageCheck, TriangleAlert, CalendarClock, CalendarDays, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ClipboardList, ClipboardX, ClipboardCheck, Tag, Minus, MessageCircle, Check, Ban } from 'lucide-react';
import { isColetaMl } from '../../utils/envioMl';
import ProdutoImagem from '../ProdutoImagem';
import { produtosAtivos } from '../OcorrenciasItemVenda';
import { STATUS_MARKETPLACE, STATUS_TAGA, MARCADORES, CANAIS, NUMEROS, ROTULO_SECAO, LARANJA, SOMBRA, SOMBRA_HOVER, isMercadoLivre, paraNumero, brl, situacaoPrazo, Selo } from '../VendaCardV2/ui';

const LOTE = 30;
const LOTE_CELULAR = 10;

const OUTROS = { chave: 'OUTROS', texto: 'Outros', scheme: 'gray', icone: CircleHelp };

const CORES = {
    gray: { fundo: 'gray.100', cor: 'gray.600' },
    orange: { fundo: 'orange.100', cor: 'orange.600' },
    blue: { fundo: 'blue.100', cor: 'blue.600' },
    green: { fundo: 'green.100', cor: 'green.600' },
    red: { fundo: 'red.100', cor: 'red.600' },
    yellow: { fundo: 'yellow.100', cor: 'yellow.700' },
    purple: { fundo: 'purple.100', cor: 'purple.600' },
    teal: { fundo: 'teal.100', cor: 'teal.600' },
    pink: { fundo: 'pink.100', cor: 'pink.600' },
};

const ESPERANDO_ENVIO = ['CONFIRMADA', 'PARA ENVIAR'];

const chaveVenda = (pedido) => `${pedido.origem}-${pedido.id_taga || pedido.id}`;

const notaEmitida = (pedido) => !(pedido.notaEmitida === false || pedido.notaEmitida === 0 || pedido.notaEmitida === '0');

const modalidade = (pedido) => {
    const modo = String(pedido.envio?.modo_envio || '');
    if (/retirada/i.test(modo)) return 'retirada';
    if (modo === 'Entrega a Combinar') return 'combinar';
    if (Number(pedido.flex) === 1) return 'flex';
    if (isColetaMl(pedido.envio)) return 'coleta';
    if (isMercadoLivre(pedido.origem)) return 'mercado_envios';
    return 'transportadora';
};

const faixaPrazo = (pedido) => {
    if (!ESPERANDO_ENVIO.includes(pedido.status)) return 'sem_prazo';
    const prazo = DateTime.fromFormat(String(pedido.data_limite_envio || ''), 'dd/MM/yyyy HH:mm', { zone: 'America/Sao_Paulo' });
    if (!prazo.isValid) return 'sem_prazo';
    const agora = DateTime.now().setZone('America/Sao_Paulo');
    if (prazo < agora) return 'atrasado';
    const dias = prazo.startOf('day').diff(agora.startOf('day'), 'days').days;
    if (dias < 1) return 'hoje';
    if (dias < 2) return 'amanha';
    return 'depois';
};

const ORDEM_DESPACHO = { pendente: 0, verificado: 1, embalado: 2, enviado: 3 };

const etapaItem = (status) => {
    const s = Number(status);
    if (s === 2) return 'problema';
    if (s === 1 || s === 5) return 'verificado';
    if (s === 3) return 'embalado';
    if (s === 4) return 'enviado';
    return 'pendente';
};

const etapaDespacho = (pedido) => {
    if (pedido.status === 'CANCELADA') return 'cancelada';
    if (pedido.status === 'EM TRÂNSITO' || pedido.status === 'CONCLUÍDA') return 'enviado';
    const etapas = produtosAtivos(pedido).map((p) => etapaItem(p.conferencia_status));
    if (!etapas.length) return 'pendente';
    if (etapas.includes('problema')) return 'problema';
    return etapas.reduce((menor, e) => (ORDEM_DESPACHO[e] < ORDEM_DESPACHO[menor] ? e : menor), 'enviado');
};

const situacaoEtiqueta = (pedido) => {
    const modo = modalidade(pedido);
    if (modo === 'retirada' || modo === 'combinar') return 'sem';
    return etiquetaPronta(pedido) ? 'pronta' : 'pendente';
};

const PRIORIDADE_LISTA = ['sem_estoque', 'rejeitado', 'sem_status', 'na_lista', 'respondido', 'parcialmente_aprovado', 'aprovado', 'ignorado'];

const situacaoLista = (pedido) => {
    const status = (pedido.produtos || []).map((p) => p.lista_compra_status?.status || 'sem_status');
    if (!status.length) return 'sem_status';
    return PRIORIDADE_LISTA.find((s) => status.includes(s)) || 'outros';
};

const coluna = (chave, texto, scheme, icone) => ({ chave, texto, scheme, icone });

export const AGRUPAMENTOS = {
    status: {
        rotulo: 'Status da venda',
        colunas: ['CONFIRMADA', 'PARA ENVIAR', 'EM TRÂNSITO', 'CONCLUÍDA', 'CANCELADA'].map((chave) => ({ chave, ...STATUS_MARKETPLACE[chave] })),
        de: (p) => p.status,
    },
    taga: {
        rotulo: 'Status TAGA',
        colunas: [coluna('1', 'Confirmada', 'green', CircleCheck), coluna('3', 'Pendente', 'yellow', Clock), coluna('2', 'Cancelada', 'red', CircleX)],
        de: (p) => String(p.status_taga),
    },
    logistica: {
        rotulo: 'Logística (despacho)',
        colunas: [
            coluna('pendente', 'Para conferir', 'gray', ClipboardList),
            coluna('problema', 'Com problema', 'red', ClipboardX),
            coluna('verificado', 'Verificado', 'blue', ClipboardCheck),
            coluna('embalado', 'Embalado', 'purple', PackageCheck),
            coluna('enviado', 'Enviado', 'green', Truck),
            coluna('cancelada', 'Cancelada', 'red', CircleX),
        ],
        de: etapaDespacho,
    },
    modalidade: {
        rotulo: 'Modalidade de envio',
        colunas: [
            coluna('flex', 'Flex', 'green', Zap),
            coluna('coleta', 'Coleta ML', 'pink', Truck),
            coluna('mercado_envios', 'Mercado Envios', 'yellow', PackageCheck),
            coluna('transportadora', 'Transportadora', 'blue', Truck),
            coluna('retirada', 'Retirada na loja', 'teal', Store),
            coluna('combinar', 'A combinar', 'orange', Handshake),
        ],
        de: modalidade,
    },
    prazo: {
        rotulo: 'Prazo de envio',
        colunas: [
            coluna('atrasado', 'Atrasado', 'red', TriangleAlert),
            coluna('hoje', 'Enviar hoje', 'orange', Clock),
            coluna('amanha', 'Amanhã', 'yellow', CalendarClock),
            coluna('depois', 'Próximos dias', 'blue', CalendarDays),
            coluna('sem_prazo', 'Já enviada ou sem prazo', 'gray', CircleCheck),
        ],
        de: faixaPrazo,
    },
    canal: {
        rotulo: 'Canal',
        colunas: [coluna('ml1', 'ML conta 1', 'yellow', ShoppingCart), coluna('ml2', 'ML conta 2', 'blue', ShoppingCart), coluna('ml3', 'ML conta 3', 'green', ShoppingCart), coluna('site', 'Site', 'orange', ShoppingCart), coluna('manual', 'Manual', 'gray', ShoppingCart)],
        de: (p) => (isMercadoLivre(p.origem) ? `ml${[2, 3].includes(Number(p.conta)) ? Number(p.conta) : 1}` : p.origem === 'ECOMMERCE' ? 'site' : p.origem === 'TAGA' ? 'manual' : 'outros'),
    },
    lista: {
        rotulo: 'Lista de compra',
        colunas: [
            coluna('sem_status', 'Sem lista', 'gray', Minus),
            coluna('na_lista', 'Na lista', 'yellow', Clock),
            coluna('respondido', 'Respondido', 'blue', MessageCircle),
            coluna('parcialmente_aprovado', 'Parcialmente aprovado', 'teal', Check),
            coluna('aprovado', 'Aprovado', 'green', CircleCheck),
            coluna('sem_estoque', 'Sem estoque', 'red', TriangleAlert),
            coluna('rejeitado', 'Rejeitado', 'red', CircleX),
            coluna('ignorado', 'Ignorado', 'gray', Ban),
        ],
        de: situacaoLista,
    },
    etiqueta: {
        rotulo: 'Etiqueta',
        colunas: [coluna('pendente', 'Etiqueta pendente', 'yellow', Tag), coluna('pronta', 'Etiqueta pronta', 'green', Tag), coluna('sem', 'Sem etiqueta (retirada ou a combinar)', 'gray', Store)],
        de: situacaoEtiqueta,
    },
    nfe: {
        rotulo: 'NF-e',
        colunas: [coluna('pendente', 'NF-e pendente', 'yellow', FileText), coluna('emitida', 'NF-e emitida', 'green', FileText)],
        de: (p) => (notaEmitida(p) ? 'emitida' : 'pendente'),
    },
};

export const AlternarVisao = ({ kanban, onChange }) => (
    <HStack spacing={0} bg="gray.100" borderRadius="md" p="2px" flex="none">
        {[[false, 'Lista', LayoutList], [true, 'Kanban', SquareKanban]].map(([valor, rotulo, icone]) => {
            const ativo = kanban === valor;
            return (
                <Button key={rotulo} size="xs" h="24px" px={2} borderRadius="sm" fontSize="11px" fontWeight="700" leftIcon={<Icon as={icone} boxSize="13px" />}
                    bg={ativo ? LARANJA : 'transparent'} color={ativo ? 'white' : 'gray.500'} boxShadow={ativo ? 'sm' : 'none'}
                    _hover={{ bg: ativo ? LARANJA : 'gray.200' }} aria-pressed={ativo} onClick={() => onChange(valor)}>
                    {rotulo}
                </Button>
            );
        })}
    </HStack>
);

const SeloCanal = ({ pedido }) => {
    const canal = CANAIS[pedido.origem] || { sigla: pedido.origem || '?', bg: 'gray.200', fg: 'gray.700' };
    const conta = isMercadoLivre(pedido.origem) && pedido.conta ? ` ${pedido.conta}` : '';
    return (
        <Box as="span" flex="none" px={1.5} h="18px" lineHeight="18px" borderRadius="md" bg={canal.bg} color={canal.fg} fontSize="10px" fontWeight="800" letterSpacing="0.02em" sx={NUMEROS}>
            {canal.sigla}{conta}
        </Box>
    );
};

const parar = (e) => e.stopPropagation();

const ESTILO_ABA = {
    display: 'flex', alignItems: 'center', gap: '3px', h: '17px', px: 2, fontSize: '9px', fontWeight: '800', letterSpacing: '0.03em',
    borderTopRadius: 'md', borderBottomRadius: 0, boxShadow: 'sm', textTransform: 'uppercase',
};

const Aba = ({ cor, icone, titulo, children }) => (
    <Box as="span" {...ESTILO_ABA} bg={`${cor}.500`} color={cor === 'yellow' ? 'yellow.900' : 'white'} {...(cor === 'yellow' ? { bg: 'yellow.300' } : {})} title={titulo}>
        <Icon as={icone} boxSize="10px" />
        {children}
    </Box>
);

const etiquetaPronta = (pedido) => {
    if (Number(pedido.pode_imprimir_etiqueta) === 1 || pedido.pode_imprimir_etiqueta === true) return true;
    const me = pedido.etiqueta_melhor_envio;
    return !!(me && (me.generated_at || ['released', 'posted', 'delivered'].includes(me.status)));
};

const Referencia = ({ rotulo, valor }) => (temTexto(valor) ? (
    <Text as="span" fontSize="11px" color="gray.500" sx={{ ...NUMEROS, overflowWrap: 'anywhere' }}>
        <Text as="span" fontWeight="700" color="gray.600">{rotulo}:</Text> {valor}
    </Text>
) : null);

const temTexto = (v) => v !== null && v !== undefined && String(v).trim() !== '';

const ProdutoKanban = ({ produto }) => {
    const preco = paraNumero(produto.preco);
    const fora = Number(produto.quantidade_cancelada || 0) + Number(produto.quantidade_devolvida || 0);
    return (
        <Flex gap={3} align="flex-start">
            <Flex direction="column" align="center" flex="none" minW="64px" gap={1}>
                <Box w="60px" h="60px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.100" bg="white">
                    <ProdutoImagem produto={produto} origem="" width="58px" height="58px" />
                </Box>
                <Text fontSize="12px" fontWeight="800" color="green.600" whiteSpace="nowrap" sx={NUMEROS}>{preco === null ? '-' : brl(preco)}</Text>
                <Text fontSize="10px" fontWeight="700" color="gray.500" whiteSpace="nowrap" sx={NUMEROS}>{produto.quantidade} un.</Text>
            </Flex>
            <Box minW={0} flex="1">
                <Box bg="gray.50" border="1px solid" borderColor="gray.100" borderRadius="md" px={2.5} py={2}>
                    <Text fontSize="12.5px" fontWeight="700" color="gray.900" lineHeight="1.3" sx={{ overflowWrap: 'anywhere' }}>{produto.nome}</Text>
                </Box>
                <Flex wrap="wrap" columnGap={2.5} rowGap={0.5} mt={1.5}>
                    <Referencia rotulo="SKU" valor={produto.id} />
                    <Referencia rotulo="Global" valor={produto.codigo} />
                    <Referencia rotulo="Forn" valor={produto.codigo_fornecedor} />
                    <Referencia rotulo="Fabr" valor={produto.codigo_fabricante} />
                    <Referencia rotulo="Marca" valor={produto.fabricante} />
                </Flex>
                {(fora > 0 || Number(produto.quantidade_taga) > 0) && (
                    <Flex wrap="wrap" gap={1} mt={1.5}>
                        {Number(produto.quantidade_cancelada) > 0 && <Selo scheme="red" icone={CircleX}>Cancelado {produto.quantidade_cancelada} de {produto.quantidade}</Selo>}
                        {Number(produto.quantidade_devolvida) > 0 && <Selo scheme="purple" icone={Undo2}>Devolvido {produto.quantidade_devolvida} de {produto.quantidade}</Selo>}
                        {Number(produto.quantidade_taga) > 0 && <Selo scheme="red" forte icone={Warehouse}>{produto.quantidade_taga} TAGA{temTexto(produto.local_estoque) ? ` · ${produto.local_estoque}` : ''}</Selo>}
                    </Flex>
                )}
            </Box>
        </Flex>
    );
};

const CardKanban = ({ pedido, onAbrir, selecionado, onSelecionar }) => {
    const statusMkt = STATUS_MARKETPLACE[pedido.status];
    const [expandido, setExpandido] = useState(false);
    const statusTaga = STATUS_TAGA[pedido.status_taga];
    const marcador = MARCADORES[pedido.marcador];
    const numero = pedido.origem === 'TAGA' ? pedido.id_taga : pedido.id;
    const total = pedido.extrato ? pedido.extrato.total_realizado : paraNumero(pedido.total);
    const cliente = pedido.dados_cliente?.nome || pedido.dados_cliente?.nickname;
    const produtos = pedido.produtos || [];
    const modo = String(pedido.envio?.modo_envio || '');
    const retirada = /retirada/i.test(modo);
    const aCombinar = modo === 'Entrega a Combinar';
    const prazo = ESPERANDO_ENVIO.includes(pedido.status) ? situacaoPrazo(pedido.data_limite_envio) : 'invalido';
    const notaOk = notaEmitida(pedido);
    const fornecedores = [...new Set(produtos.map((p) => p.fornecedor).filter(temTexto))];
    const abrir = () => onAbrir(pedido);
    const cancelada = pedido.status === 'CANCELADA';

    return (
        <Box position="relative" pt="17px">
            <HStack spacing="2px" position="absolute" top={0} left="10px">
                {!cancelada && <Aba cor={notaOk ? 'green' : 'yellow'} icone={FileText} titulo={notaOk ? 'NF-e emitida' : 'NF-e pendente'}>NF-e</Aba>}
                {!cancelada && (retirada
                    ? <Aba cor="teal" icone={Store} titulo="Retirada na loja">Retirada</Aba>
                    : aCombinar
                        ? <Aba cor="orange" icone={Handshake} titulo="Entrega a combinar">A combinar</Aba>
                        : <Aba cor={etiquetaPronta(pedido) ? 'green' : 'yellow'} icone={Truck} titulo={etiquetaPronta(pedido) ? 'Etiqueta pronta' : 'Etiqueta pendente'}>Etiq</Aba>)}
                {produtos.length > 1 && <Aba cor="purple" icone={Boxes} titulo={`${produtos.length} produtos na venda`}>({produtos.length})</Aba>}
                {Number(pedido.flex) === 1 && <Aba cor="green" icone={Zap} titulo="Mercado Envios Flex">Flex</Aba>}
            </HStack>
            <Box role="button" tabIndex={0} aria-label={`Abrir venda ${numero}`} onClick={abrir}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); abrir(); } }}
                w="100%" textAlign="left" bg="white" border="1px solid" borderColor={selecionado ? 'orange.300' : (prazo === 'vencido' ? 'red.200' : 'gray.200')} borderTop="3px solid" borderTopColor={prazo === 'vencido' ? 'red.400' : LARANJA} borderRadius="lg" cursor="pointer"
                boxShadow={selecionado ? '0 0 0 3px rgba(246, 134, 51, 0.18)' : SOMBRA} p={3} transition="box-shadow 0.15s, border-color 0.15s, transform 0.15s"
                _hover={{ boxShadow: selecionado ? '0 0 0 3px rgba(246, 134, 51, 0.18)' : SOMBRA_HOVER, transform: 'translateY(-1px)' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'orange.300', outlineOffset: '2px' }}>
                <Flex align="flex-start" gap={2}>
                    <Box onClick={parar} display="flex" flex="none" pt="1px">
                        <Checkbox colorScheme="orange" isChecked={selecionado} onChange={() => onSelecionar(pedido.id)} aria-label={`Selecionar venda ${numero}`} />
                    </Box>
                    <Box flex="1" minW={0}>
                        <Text fontSize="13px" fontWeight="800" color="gray.900" lineHeight="1.25" sx={{ ...NUMEROS, overflowWrap: 'anywhere' }}>#{numero}</Text>
                        {pedido.pack_id && <Text fontSize="11px" color="gray.500" sx={{ ...NUMEROS, overflowWrap: 'anywhere' }}>Pack {pedido.pack_id}</Text>}
                    </Box>
                </Flex>
                <Flex wrap="wrap" gap={1} mt={2} align="center">
                    <SeloCanal pedido={pedido} />
                    {statusMkt && <Selo scheme={statusMkt.scheme} icone={statusMkt.icone}>{CANAIS[pedido.origem]?.sigla === 'SITE' ? 'Site' : (CANAIS[pedido.origem]?.sigla || 'Canal')} · {statusMkt.texto}</Selo>}
                    {fornecedores.map((f) => (
                        <Box key={f} as="span" px={1.5} h="18px" lineHeight="16px" borderRadius="md" border="1px solid" borderColor="teal.400" color="teal.600" fontSize="10px" fontWeight="800" textTransform="uppercase">{f}</Box>
                    ))}
                    {statusTaga && pedido.status_taga !== 1 && <Selo scheme={statusTaga.scheme} forte ponto>TAGA · {statusTaga.texto}</Selo>}
                    {marcador && <Selo scheme={marcador.scheme} forte icone={marcador.icone}>{marcador.texto}</Selo>}
                    {isColetaMl(pedido.envio) && <Selo scheme="pink" forte icone={Truck}>Coleta</Selo>}
                    <Text fontSize="14px" fontWeight="800" color="gray.900" whiteSpace="nowrap" ml="auto" pl={1} sx={NUMEROS}>{brl(total)}</Text>
                </Flex>
                <Flex direction="column" gap={3} mt={3}>
                    {(expandido ? produtos : produtos.slice(0, 1)).map((p, i) => <ProdutoKanban key={p.item_venda_id ?? `${p.id}-${i}`} produto={p} />)}
                    {produtos.length > 1 && (
                        <Button size="xs" variant="ghost" alignSelf="flex-start" h="24px" px={2} ml={-2} color="gray.600" fontSize="11px" fontWeight="700"
                            rightIcon={<Icon as={expandido ? ChevronUp : ChevronDown} boxSize="13px" />} aria-expanded={expandido}
                            onClick={(e) => { e.stopPropagation(); setExpandido((v) => !v); }}>
                            {expandido ? 'Recolher produtos' : `Ver mais ${produtos.length - 1} ${produtos.length - 1 === 1 ? 'produto' : 'produtos'}`}
                        </Button>
                    )}
                </Flex>
                <Flex align="center" justify="space-between" columnGap={3} rowGap={1} wrap="wrap" mt={3} pt={2.5} borderTop="1px solid" borderColor="gray.100">
                    <HStack spacing={3}>
                        {pedido.data_hora && (
                            <HStack spacing={1} color="gray.500" title="Data da venda">
                                <Icon as={Calendar} boxSize="12px" />
                                <Text fontSize="11px" whiteSpace="nowrap" sx={NUMEROS}>{pedido.data_hora}</Text>
                            </HStack>
                        )}
                        {prazo !== 'invalido' && (
                            <HStack spacing={1} color={prazo === 'vencido' ? 'red.600' : 'orange.600'} title="Limite de despacho">
                                <Icon as={Clock} boxSize="12px" />
                                <Text fontSize="11px" fontWeight="800" whiteSpace="nowrap" sx={NUMEROS}>{pedido.data_limite_envio}</Text>
                            </HStack>
                        )}
                    </HStack>
                    <HStack spacing={1} color="gray.500" minW={0} ml="auto" title="Cliente">
                        <Icon as={User} boxSize="12px" flex="none" />
                        <Text fontSize="11px" fontWeight="700" color="gray.700" sx={{ overflowWrap: 'anywhere' }}>{cliente || 'Cliente não informado'}</Text>
                    </HStack>
                </Flex>
            </Box>
        </Box>
    );
};

const VendasKanban = ({ pedidos, isMobile, renderVenda, checkboxesState = [], onSelecionar }) => {
    const [limites, setLimites] = useState({});
    const [agrupar, setAgrupar] = useState('status');
    const trilhoRef = useRef(null);
    const [rolagem, setRolagem] = useState({ esquerda: false, direita: false });

    const medirRolagem = useCallback(() => {
        const el = trilhoRef.current;
        if (!el) return;
        const esquerda = el.scrollLeft > 2;
        const direita = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
        setRolagem((atual) => (atual.esquerda === esquerda && atual.direita === direita ? atual : { esquerda, direita }));
    }, []);

    useEffect(() => {
        const el = trilhoRef.current;
        if (!el) return undefined;
        medirRolagem();
        el.addEventListener('scroll', medirRolagem, { passive: true });
        const observador = new ResizeObserver(medirRolagem);
        observador.observe(el);
        return () => {
            el.removeEventListener('scroll', medirRolagem);
            observador.disconnect();
        };
    }, [medirRolagem]);

    const rolar = (sentido) => {
        const el = trilhoRef.current;
        if (!el) return;
        const coluna = el.querySelector('[data-coluna]');
        const passo = coluna ? coluna.getBoundingClientRect().width + 12 : el.clientWidth * 0.8;
        el.scrollBy({ left: sentido * passo, behavior: 'smooth' });
    };
    const [abertoChave, setAbertoChave] = useState(null);
    const ultimoAberto = useRef(null);

    useEffect(() => {
        setLimites({});
    }, [pedidos, agrupar]);

    const colunas = useMemo(() => {
        const { colunas: definicao, de } = AGRUPAMENTOS[agrupar];
        const grupos = Object.fromEntries(definicao.map((c) => [c.chave, []]));
        const outros = [];
        (pedidos || []).forEach((p) => {
            const chave = de(p);
            if (grupos[chave]) grupos[chave].push(p);
            else outros.push(p);
        });
        const lista = definicao.map((c) => ({ ...c, pedidos: grupos[c.chave] }));
        return outros.length ? [...lista, { ...OUTROS, pedidos: outros }] : lista;
    }, [pedidos, agrupar]);

    useEffect(() => {
        if (trilhoRef.current) trilhoRef.current.scrollLeft = 0;
        medirRolagem();
    }, [colunas, medirRolagem]);

    const aberto = abertoChave ? ((pedidos || []).find((p) => chaveVenda(p) === abertoChave) || ultimoAberto.current) : null;
    ultimoAberto.current = aberto;

    return (
        <>
            <Flex align="center" justify="space-between" gap={3} wrap="wrap" mt={2} px={{ base: 1, md: 0 }} textAlign="left">
                <HStack spacing={2}>
                    <IconButton aria-label="Colunas anteriores" icon={<Icon as={ChevronLeft} boxSize="18px" />} size="sm" variant="outline" bg="white" borderColor="gray.200"
                        color="gray.700" borderRadius="md" isDisabled={!rolagem.esquerda} onClick={() => rolar(-1)} />
                    <Icon as={Columns3} boxSize="14px" color="gray.500" />
                    <Text {...ROTULO_SECAO} color="gray.500" as="label" htmlFor="kanban-colunas">Colunas por</Text>
                    <Select id="kanban-colunas" size="sm" w="auto" minW="190px" bg="white" borderColor="gray.200" borderRadius="md" fontSize="13px" fontWeight="600"
                        value={agrupar} onChange={(e) => setAgrupar(e.target.value)}>
                        {Object.entries(AGRUPAMENTOS).map(([valor, a]) => <option key={valor} value={valor}>{a.rotulo}</option>)}
                    </Select>
                </HStack>
                <HStack spacing={2}>
                    <Text fontSize="12px" color="gray.500" sx={NUMEROS}>{(pedidos || []).length} venda(s)</Text>
                    <IconButton aria-label="Próximas colunas" icon={<Icon as={ChevronRight} boxSize="18px" />} size="sm" variant="outline" bg="white" borderColor="gray.200"
                        color="gray.700" borderRadius="md" isDisabled={!rolagem.direita} onClick={() => rolar(1)} />
                </HStack>
            </Flex>
            <Flex ref={trilhoRef} gap={3} align="flex-start" overflowX="auto" pb={3} pt={2} textAlign="left"
                sx={{ scrollSnapType: isMobile ? 'x mandatory' : undefined, WebkitOverflowScrolling: 'touch' }}>
                {colunas.map((col) => {
                    const cor = CORES[col.scheme] || CORES.gray;
                    const lote = isMobile ? LOTE_CELULAR : LOTE;
                    const limite = limites[col.chave] || lote;
                    const faltam = col.pedidos.length - limite;
                    return (
                        <Box key={col.chave} data-coluna={col.chave} flex={isMobile ? '0 0 88%' : (col.pedidos.length ? '1 0 290px' : '0 0 190px')} minW={isMobile ? undefined : (col.pedidos.length ? '290px' : '190px')} bg="#E4E8EE" borderRadius="xl" p={2}
                            sx={{ scrollSnapAlign: 'start' }}>
                            <Flex align="center" justify="space-between" px={1.5} pt={1} pb={2.5}>
                                <HStack spacing={2}>
                                    <Flex w="22px" h="22px" borderRadius="md" bg={cor.fundo} color={cor.cor} align="center" justify="center">
                                        <Icon as={col.icone} boxSize="13px" strokeWidth={2.25} />
                                    </Flex>
                                    <Text {...ROTULO_SECAO} color="gray.700">{col.texto}</Text>
                                </HStack>
                                <Box data-contador px={2} borderRadius="full" bg="white" color="gray.700" fontSize="11px" fontWeight="800" lineHeight="20px" sx={NUMEROS}>{col.pedidos.length}</Box>
                            </Flex>
                            <Flex direction="column" gap={2}>
                                {col.pedidos.length === 0 && (
                                    <Text fontSize="12px" color="gray.500" textAlign="center" py={6}>Nenhuma venda</Text>
                                )}
                                {col.pedidos.slice(0, limite).map((pedido) => (
                                    <CardKanban key={chaveVenda(pedido)} pedido={pedido} onAbrir={(p) => setAbertoChave(chaveVenda(p))} selecionado={checkboxesState.includes(pedido.id)} onSelecionar={onSelecionar} />
                                ))}
                                {faltam > 0 && (
                                    <Button size="sm" variant="ghost" color="gray.600" fontWeight="600" fontSize="12px" _hover={{ bg: 'whiteAlpha.700' }}
                                        onClick={() => setLimites((atual) => ({ ...atual, [col.chave]: limite + lote }))}>
                                        Carregar mais {Math.min(lote, faltam)} de {faltam}
                                    </Button>
                                )}
                            </Flex>
                        </Box>
                    );
                })}
            </Flex>
            {aberto && renderVenda(aberto, () => setAbertoChave(null))}
        </>
    );
};

export default VendasKanban;
